import React, { useEffect, useState } from "react";
import { Check, BookOpen, Lock, ChevronDown, FileText, Download, Play, Award } from "lucide-react";
import DOMPurify from "dompurify";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { useToast } from "../hooks/use-toast";
import api from "../api";

function Card({ children, className = "" }) {
  return <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6 ${className}`}>{children}</div>;
}

function embedUrl(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vim = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vim) return `https://player.vimeo.com/video/${vim[1]}`;
  return null;
}

function KnowledgeCheck({ module, onPassed }) {
  const { toast } = useToast();
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (Object.keys(answers).length < module.questions.length) {
      toast({ title: "Answer all questions", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/training/${module.id}/check`, { answers });
      setResult(data);
      if (data.passed) { toast({ title: `Passed with ${data.score}%!` }); onPassed(); }
      else toast({ title: `Scored ${data.score}% — need ${data.threshold}%`, description: "Review the lesson and try again.", variant: "destructive" });
    } catch (e) {
      toast({ title: "Error", description: e.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-5 rounded-xl border border-violet-100 bg-violet-50/40 p-4" data-testid={`knowledge-check-${module.id}`}>
      <div className="mb-3 text-sm font-bold text-slate-900">Knowledge check · pass {module.passing_score}% to complete</div>
      <div className="space-y-4">
        {module.questions.map((q, qi) => (
          <div key={q.id}>
            <div className="text-sm font-medium text-slate-800">{qi + 1}. {q.question}</div>
            <div className="mt-2 space-y-1.5">
              {q.options.map((opt, oi) => (
                <label key={oi} className="flex cursor-pointer items-center gap-2 text-sm text-slate-600">
                  <input type="radio" name={`${module.id}-${q.id}`} checked={answers[q.id] === oi} onChange={() => setAnswers((a) => ({ ...a, [q.id]: oi }))} className="accent-violet-600" data-testid={`kc-opt-${module.id}-${qi}-${oi}`} />
                  {opt}
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
      {result && (
        <div className={`mt-3 rounded-lg px-3 py-2 text-sm font-medium ${result.passed ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
          You scored {result.score}% ({result.correct}/{result.total}). {result.passed ? "Module complete!" : `You need ${result.threshold}% to pass.`}
        </div>
      )}
      <Button onClick={submit} disabled={busy} data-testid={`submit-check-${module.id}`} className="btn-primary-gradient mt-3 border-0 text-white">
        {busy ? "Submitting…" : "Submit knowledge check"}
      </Button>
    </div>
  );
}

export default function Training() {
  const { toast } = useToast();
  const [modules, setModules] = useState([]);
  const [done, setDone] = useState([]);
  const [percent, setPercent] = useState(0);
  const [badges, setBadges] = useState([]);
  const [openId, setOpenId] = useState(null);

  const load = () => api.get("/training").then((r) => {
    setModules(r.data.modules);
    setDone(r.data.done || []);
    setPercent(r.data.percent || 0);
    setBadges(r.data.badges || []);
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const isDone = (id) => done.includes(id);
  const firstIncomplete = modules.findIndex((m) => !isDone(m.id));
  const isUnlocked = (i) => firstIncomplete === -1 || i <= firstIncomplete;

  const markComplete = async (m) => {
    try {
      await api.post(`/training/${m.id}/complete`, { done: true });
      toast({ title: "Module completed" });
      load();
    } catch (e) {
      toast({ title: "Error", description: e.response?.data?.detail || "", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-slate-900">Training curriculum</h2>
            <p className="text-sm text-slate-500">Complete all modules in order to unlock the final assessment.</p>
          </div>
          <div className="text-2xl font-extrabold text-violet-600">{percent}%</div>
        </div>
        <Progress value={percent} className="mt-4 h-2" />
      </Card>

      {badges.length > 0 && (
        <Card data-testid="my-badges">
          <div className="mb-3 flex items-center gap-2">
            <Award className="h-4 w-4 text-amber-500" />
            <h3 className="text-sm font-bold text-slate-900">My badges</h3>
          </div>
          <div className="flex flex-wrap gap-3">
            {badges.map((b) => (
              <div key={b.module_id} className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2" data-testid={`badge-${b.module_id}`}>
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-400 text-white"><Award className="h-4 w-4" /></div>
                <div>
                  <div className="text-sm font-semibold text-slate-800">{b.title}</div>
                  <div className="text-xs text-slate-500">{new Date(b.earned_at).toLocaleDateString()}{b.score != null ? ` · ${b.score}%` : ""}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="space-y-3">
        {modules.map((m, i) => {
          const unlocked = isUnlocked(i);
          const open = openId === m.id;
          const videos = (m.videos || []).map(embedUrl).filter(Boolean);
          return (
            <Card key={m.id} data-testid={`training-module-${m.id}`}>
              <button
                onClick={() => unlocked && setOpenId(open ? null : m.id)}
                disabled={!unlocked}
                className="flex w-full items-center justify-between gap-4 text-left disabled:cursor-not-allowed"
                data-testid={`module-header-${m.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${isDone(m.id) ? "bg-emerald-500 text-white" : unlocked ? "bg-violet-100 text-violet-600" : "bg-slate-100 text-slate-400"}`}>
                    {isDone(m.id) ? <Check className="h-5 w-5" /> : unlocked ? <BookOpen className="h-5 w-5" /> : <Lock className="h-5 w-5" />}
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900">{i + 1}. {m.title}</div>
                    <div className="text-sm text-slate-500">{m.description || (unlocked ? "" : "Complete the previous module to unlock")}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {isDone(m.id) && <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">Completed</span>}
                  {unlocked && <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />}
                </div>
              </button>

              {open && (
                <div className="mt-5 border-t border-slate-100 pt-5" data-testid={`module-body-${m.id}`}>
                  {m.content_html && (
                    <div className="lesson-content" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(m.content_html) }} />
                  )}

                  {videos.map((v, vi) => (
                    <div key={vi} className="mt-4 aspect-video w-full overflow-hidden rounded-xl border border-slate-200">
                      <iframe src={v} title={`${m.title} video ${vi + 1}`} className="h-full w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                    </div>
                  ))}

                  {(m.attachments || []).length > 0 && (
                    <div className="mt-4 space-y-2">
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Resources</div>
                      {m.attachments.map((a, ai) => (
                        a.kind === "image" ? (
                          <img key={ai} src={a.url} alt={a.name} className="max-w-full rounded-lg border border-slate-200" />
                        ) : (
                          <a key={ai} href={a.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2 text-sm text-violet-700 hover:bg-slate-100" data-testid={`resource-${m.id}-${ai}`}>
                            <FileText className="h-4 w-4" /> {a.name} <Download className="ml-auto h-4 w-4 text-slate-400" />
                          </a>
                        )
                      ))}
                    </div>
                  )}

                  {m.has_check ? (
                    isDone(m.id) ? (
                      <div className="mt-5 rounded-lg bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700">Knowledge check passed{m.score != null ? ` (${m.score}%)` : ""}.</div>
                    ) : (
                      <KnowledgeCheck module={m} onPassed={load} />
                    )
                  ) : (
                    <Button onClick={() => markComplete(m)} disabled={isDone(m.id)} data-testid={`mark-complete-${m.id}`} className={`mt-5 ${isDone(m.id) ? "" : "btn-primary-gradient border-0 text-white"}`} variant={isDone(m.id) ? "outline" : "default"}>
                      {isDone(m.id) ? "Completed" : "Mark complete"}
                    </Button>
                  )}
                </div>
              )}
            </Card>
          );
        })}
        {modules.length === 0 && <Card><p className="py-6 text-center text-sm text-slate-400">No training modules are published yet. Check back soon.</p></Card>}
      </div>
    </div>
  );
}
